import { Box } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import {
  ISiteSection,
  useGetSiteSectionsQuery,
  useDeleteSiteSectionMutation,
  GetSiteSectionsDocument,
  IGetSiteSectionsQuery
} from '../../../generated/graphql';
import Loader from '../../Common/Loader';
import OverviewTable, { IHeadCell } from '../../Common/Tables/OverviewTable';
import SiteSectionForm from './SiteSectionForm';

const SiteSectionList: React.FunctionComponent = () => {
  const { data, loading } = useGetSiteSectionsQuery();
  const [allData, setAllData] = useState<ISiteSection[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [siteSection, setSiteSection] = useState<ISiteSection | null>(null);
  const { dispatch } = useContext(AppContext);
  const [deleteSiteSection] = useDeleteSiteSectionMutation();

  useEffect(() => {
    if (data && data.result) {
      setAllData(data.result as ISiteSection[]);
    }
  }, [data]);

  const handleRemove = (rowData: ISiteSection) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name} site section?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (rowData: ISiteSection) => {
    if (!rowData.id) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteSiteSection({
      variables: {
        id: rowData.id
      },
      update(cache) {
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Site Section ${rowData.name} deleted`, severity: 'success' }
        });

        const sectionsQuery = cache.readQuery({
          query: GetSiteSectionsDocument
        }) as IGetSiteSectionsQuery;
        if (sectionsQuery.result) {
          const cachedSiteSection = sectionsQuery.result.findIndex((s) => s?.id === rowData.id);
          if (cachedSiteSection > -1) {
            const data = sectionsQuery.result.filter(function (el) {
              return el?.id !== rowData.id;
            });
            cache.writeQuery({
              query: GetSiteSectionsDocument,
              data: { result: data }
            });
          }
        }
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: e.message, severity: 'error', autoHide: 6000 }
      });
    });
  };

  const columns = [
    {
      title: 'Details',
      width: 63,
      sorting: false
    },
    {
      title: 'Id',
      field: 'id',
      type: 'numeric',
      width: 130
    },
    {
      title: 'Section Name',
      field: 'name',
      width: 1455
    },
    {
      title: 'Remove',
      width: 73,
      sorting: false
    }
  ] as IHeadCell[];

  const handleDetails = (rowData: ISiteSection) => {
    setSiteSection(rowData);
    setOpenForm(true);
  };

  const handleClose = () => {
    setSiteSection(null);
    setOpenForm(false);
  };

  return (
    <Box className="overviewPage" sx={{ height: '100%' }}>
      <Box className="print\:w-full">
        <OverviewTable
          columns={columns}
          data={allData}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={handleRemove}
          pageSize={15}
          createButtonProps={{
            id: 'create-site-section-button',
            onClick: () => setOpenForm(true),
            text: 'Create Section',
            disabled: false
          }}
        />
        {openForm && <SiteSectionForm siteSectionInput={siteSection} handleClose={handleClose} />}
        <Loader loading={loading} />
      </Box>
    </Box>
  );
};

export default SiteSectionList;
